// Shared add/edit dialog for date events. Mounted once at the app root
// inside DateEventDialogProvider; opened from anywhere via
// useDateEventDialog().open(eventOrNew). Logic moved verbatim out of
// EventsSection.tsx so the calendar's double-click gesture can pop the
// editor inline (instead of navigating to Settings first).

import { Show, createEffect, createMemo, createSignal } from "solid-js";
import { Portal } from "solid-js/web";
import type { DateEvent } from "../../../../backend/shared";
import { putDateEvents } from "../../shared/api/endpoints";
import { useCalendar } from "../calendar/store";
import { buildPortCountries } from "../calendar/date-events";
import { useModalDismiss } from "../../shared/hooks/useModalDismiss";
import { DateField } from "../../shared/inputs/DateField";
import { TextTooltip } from "../../shared/inputs/TextTooltip";
import { TimeField } from "../../shared/inputs/TimeField";
import { Combobox } from "../settings/Combobox";
import { SearchableSelect, type SelectOption } from "../settings/SearchableSelect";
import { countryName } from "../settings/country-name";
import { getCache, setEventKindColors } from "../../shared/settings/cache";
import { useDateEventDialog } from "./store";
import s from "../settings/Settings.module.css";

function strippedTerminalLabel(parent: string, port: string): string {
  if (port.startsWith(parent)) {
    const tail = port.slice(parent.length).replace(/^\s*-\s*/, "");
    return tail || port;
  }
  return port;
}

function kindColour(kind: string): string {
  return getCache().eventKindColors[kind] ?? "#94a3b8";
}
function writeKindColour(kind: string, hex: string) {
  setEventKindColors({ ...getCache().eventKindColors, [kind]: hex });
}

function pruneOrphanKindColours(events: DateEvent[]) {
  const inUse = new Set(events.map((e) => e.kind));
  const current = getCache().eventKindColors;
  const pruned: Record<string, string> = {};
  for (const [k, v] of Object.entries(current)) {
    if (inUse.has(k)) pruned[k] = v;
  }
  if (Object.keys(pruned).length !== Object.keys(current).length) {
    setEventKindColors(pruned);
  }
}

export function DateEventDialog() {
  const cal = useCalendar();
  const dialog = useDateEventDialog();

  const [editingId, setEditingId] = createSignal<number | null>(null);
  const [fSource, setFSource] = createSignal<"user" | "holiday">("user");
  const [fCountry, setFCountry] = createSignal("");
  const [fPort, setFPort] = createSignal("");
  const [fTerminal, setFTerminal] = createSignal("");
  const [fStart, setFStart] = createSignal("");
  const [fEnd, setFEnd] = createSignal("");
  const [fStartTime, setFStartTime] = createSignal("");
  const [fEndTime, setFEndTime] = createSignal("");
  const [fKind, setFKind] = createSignal("");
  const [fDesc, setFDesc] = createSignal("");
  const [fKindColor, setFKindColor] = createSignal("#94a3b8");
  const [saving, setSaving] = createSignal(false);
  const [err, setErr] = createSignal<string | null>(null);
  const [confirmDelete, setConfirmDelete] = createSignal(false);

  function setKindWithColour(k: string) {
    setFKind(k);
    setFKindColor(k ? kindColour(k) : "#94a3b8");
  }

  function resetForm() {
    setFCountry(""); setFPort(""); setFTerminal("");
    setFStart(""); setFEnd("");
    setFStartTime(""); setFEndTime("");
    setFKind(""); setFDesc("");
    setFKindColor("#94a3b8");
    setErr(null);
    setConfirmDelete(false);
  }

  const portCountries = createMemo(() => buildPortCountries(cal.state.portRefs));

  function scopeOf(ev: { country: string; portGroup: string; portName: string }) {
    const portName = ev.portName;
    const portGroup = ev.portGroup || (portName ? cal.state.portToGroup[portName] ?? "" : "");
    const country = ev.country || (portName ? portCountries().get(portName) ?? "" : "");
    return { country, portGroup, portName };
  }

  // Watch the global target signal — populate the form when it changes.
  createEffect(() => {
    const target = dialog.target();
    if (target === null) return;
    if (target.mode === "new") {
      setEditingId(null);
      setFSource("user");
      resetForm();
      // Apply preset overrides on top of the cleared form. The preset
      // shape mirrors DateEvent so callers can hand it (port, country,
      // dates, etc.) directly — useful for the calendar's dblclick-on-
      // empty-cell gesture which pre-fills the row's port and the day.
      const p = target.preset;
      if (p.country !== undefined) setFCountry(p.country);
      if (p.portGroup !== undefined) setFPort(p.portGroup);
      if (p.portName !== undefined) setFTerminal(p.portName);
      if (p.startDate !== undefined) setFStart(p.startDate);
      if (p.endDate !== undefined) setFEnd(p.endDate);
      if (p.startTime !== undefined) setFStartTime(p.startTime ?? "");
      if (p.endTime !== undefined) setFEndTime(p.endTime ?? "");
      if (p.kind !== undefined) {
        setFKind(p.kind);
        setFKindColor(kindColour(p.kind));
      }
      if (p.description !== undefined) setFDesc(p.description);
      return;
    }
    const ev = target.event;
    const scope = scopeOf(ev);
    setEditingId(ev.id);
    setFSource(ev.source);
    setFCountry(scope.country);
    setFPort(scope.portGroup);
    setFTerminal(scope.portName);
    setFStart(ev.startDate);
    setFEnd(ev.endDate);
    setFStartTime(ev.startTime ?? "");
    setFEndTime(ev.endTime ?? "");
    setFKind(ev.kind);
    setFKindColor(kindColour(ev.kind));
    setFDesc(ev.description);
    setErr(null);
    setConfirmDelete(false);
  });

  function close() {
    dialog.close();
    setConfirmDelete(false);
  }

  // Escape closes the dialog regardless of focus position. Gated by
  // dialog.target() so the listener stays installed across the
  // provider's lifetime but only fires while a dialog is showing.
  useModalDismiss({ onClose: close, isOpen: () => dialog.target() !== null });

  function portsInCountry(country: string): string[] {
    if (!country) return [];
    const pc = portCountries();
    const out: string[] = [];
    for (const g of cal.state.portGroups) {
      let groupCountry: string | null = null;
      for (const p of g.ports) {
        const c = pc.get(p);
        if (c) { groupCountry = c; break; }
      }
      if (groupCountry === country) out.push(g.name);
    }
    return out;
  }

  function terminalsInPort(portGroup: string): string[] {
    if (!portGroup) return [];
    const g = cal.state.portGroups.find((g) => g.name === portGroup);
    return g ? [...g.ports] : [];
  }

  const knownKinds = createMemo(() => {
    const set = new Set<string>(Object.keys(getCache().eventKindColors));
    for (const e of cal.state.dateEvents) set.add(e.kind);
    return [...set].sort((a, b) => a.localeCompare(b));
  });

  const countryOptions = createMemo<SelectOption[]>(() => {
    const set = new Set<string>();
    for (const c of portCountries().values()) set.add(c);
    for (const ev of cal.state.dateEvents) if (ev.country) set.add(ev.country);
    return [...set]
      .sort((a, b) => countryName(a).localeCompare(countryName(b)))
      .map((c) => ({ value: c, label: countryName(c) }));
  });
  const portOptions = createMemo<SelectOption[]>(() =>
    portsInCountry(fCountry())
      .sort((a, b) => a.localeCompare(b))
      .map((p) => ({ value: p, label: p })),
  );
  const terminalOptions = createMemo<SelectOption[]>(() =>
    terminalsInPort(fPort())
      .sort((a, b) => a.localeCompare(b))
      .map((t) => ({ value: t, label: strippedTerminalLabel(fPort(), t) })),
  );

  function fullPayload(list: DateEvent[]): Omit<DateEvent, "id">[] {
    return list.map(({ id: _id, ...rest }) => rest);
  }

  async function saveAll(next: Omit<DateEvent, "id">[]) {
    const written = await putDateEvents(next);
    cal.setDateEvents(written);
    pruneOrphanKindColours(written);
  }

  async function onSave(e: Event) {
    e.preventDefault();
    if (!fCountry() || !fStart() || !fEnd() || !fKind()) {
      setErr("Country, start, end (DD-MM-YYYY), and kind are required");
      return;
    }
    setSaving(true); setErr(null);
    try {
      const k = fKind();
      if (k && fKindColor() !== kindColour(k)) {
        writeKindColour(k, fKindColor());
      }
      const editId = editingId();
      const draft: Omit<DateEvent, "id"> = {
        source: fSource(),
        country: fCountry(),
        portGroup: fPort(),
        portName: fTerminal(),
        startDate: fStart(),
        endDate: fEnd(),
        startTime: fStartTime() || null,
        endTime: fEndTime() || null,
        kind: fKind(),
        description: fDesc(),
      };
      let next: Omit<DateEvent, "id">[];
      if (editId !== null) {
        next = cal.state.dateEvents.map((ev) => ev.id === editId
          ? draft
          : {
              source: ev.source,
              country: ev.country, portGroup: ev.portGroup, portName: ev.portName,
              startDate: ev.startDate, endDate: ev.endDate,
              startTime: ev.startTime, endTime: ev.endTime,
              kind: ev.kind, description: ev.description,
            },
        );
      } else {
        next = [...fullPayload(cal.state.dateEvents), draft];
      }
      await saveAll(next);
      close();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setSaving(false);
    }
  }

  async function onDelete(id: number) {
    setSaving(true);
    try {
      const next = fullPayload(cal.state.dateEvents.filter((e) => e.id !== id));
      await saveAll(next);
      close();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <Show when={dialog.target() !== null}>
      <Portal>
        <div class={s.dialogBackdrop} onMouseDown={close}>
          <div
            class={s.dialogCard}
            role="dialog"
            aria-label={editingId() !== null ? "Edit date event" : "Add date event"}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <header class={s.dialogHead}>
              <h3 class={s.dialogTitle}>
                {editingId() !== null ? "Edit event" : "Add event"}
              </h3>
              <button type="button" class={s.dialogClose} aria-label="Close" onClick={close}>×</button>
            </header>
            <form class={s.dialogForm} onSubmit={onSave}>
              <label class={s.formLabel}>
                Country *
                <SearchableSelect
                  value={fCountry}
                  setValue={(v) => { setFCountry(v); setFPort(""); setFTerminal(""); }}
                  options={countryOptions}
                  placeholder="Select a country…"
                />
              </label>
              <label class={s.formLabel}>
                Port
                <SearchableSelect
                  value={fPort}
                  setValue={(v) => { setFPort(v); setFTerminal(""); }}
                  options={portOptions}
                  disabled={() => !fCountry()}
                  emptyLabel="— All ports in country —"
                  placeholder={fCountry() ? "Select a port…" : "Pick a country first"}
                />
              </label>
              <label class={s.formLabel}>
                Terminal
                <SearchableSelect
                  value={fTerminal}
                  setValue={setFTerminal}
                  options={terminalOptions}
                  disabled={() => !fPort()}
                  emptyLabel="— All terminals in port —"
                  placeholder={fPort() ? "Select a terminal…" : "Pick a port first"}
                />
              </label>
              <div class={s.formRow}>
                <label class={s.formLabel}>
                  Start *
                  <div class={s.dateTimeStack}>
                    <DateField value={fStart} setValue={setFStart} ariaLabel="Start date" />
                    <TextTooltip
                      text="Start time"
                      description="Optional — HH:MM, 24h. Leave empty for start of day"
                    >
                      <TimeField
                        value={fStartTime}
                        setValue={setFStartTime}
                        ariaLabel="Start time (optional — leave empty for start of day)"
                      />
                    </TextTooltip>
                  </div>
                </label>
                <label class={s.formLabel}>
                  End *
                  <div class={s.dateTimeStack}>
                    <DateField value={fEnd} setValue={setFEnd} ariaLabel="End date" />
                    <TextTooltip
                      text="End time"
                      description="Optional — HH:MM, 24h. Leave empty for end of day"
                    >
                      <TimeField
                        value={fEndTime}
                        setValue={setFEndTime}
                        ariaLabel="End time (optional — leave empty for end of day)"
                      />
                    </TextTooltip>
                  </div>
                </label>
              </div>
              <label class={s.formLabel}>
                Kind *
                <div class={s.formKindRow}>
                  <Combobox
                    value={fKind}
                    setValue={setKindWithColour}
                    options={knownKinds}
                    placeholder="Blockage, Road works, …"
                  />
                  <TextTooltip
                    text={fKind() ? `Colour for "${fKind()}"` : "Kind colour"}
                    description={fKind()
                      ? "Click to change (applied on Save)"
                      : "Pick a kind first"}
                    stripe={fKind() ? fKindColor() : undefined}
                  >
                  <span
                    class={s.formKindSwatch}
                    style={{ "background-color": fKind() ? fKindColor() : "transparent" }}
                    aria-disabled={fKind() ? undefined : "true"}
                  >
                    <input
                      type="color"
                      value={fKindColor()}
                      disabled={!fKind()}
                      aria-label={fKind() ? `Colour for ${fKind()}` : "Colour (disabled — pick a kind first)"}
                      onInput={(e) => setFKindColor(e.currentTarget.value)}
                    />
                  </span>
                  </TextTooltip>
                </div>
              </label>
              <label class={s.formLabel}>
                Description
                <input
                  type="text"
                  value={fDesc()}
                  onInput={(e) => setFDesc(e.currentTarget.value)}
                  placeholder="Optional"
                />
              </label>
              <Show when={err()}><div class={s.formError}>{err()}</div></Show>
              <Show
                when={confirmDelete()}
                fallback={
                  <div class={s.dialogActions}>
                    <Show when={editingId() !== null}>
                      <button
                        type="button"
                        class={s.dangerBtn}
                        disabled={saving()}
                        onClick={() => setConfirmDelete(true)}
                      >
                        Delete
                      </button>
                    </Show>
                    <span class={s.dialogActionsSpacer} />
                    <button type="button" class={s.cancelBtn} onClick={close}>
                      Cancel
                    </button>
                    <button type="submit" class={s.primary} disabled={saving()}>
                      {saving() ? "Saving…" : editingId() !== null ? "Save" : "Add"}
                    </button>
                  </div>
                }
              >
                <div class={s.confirmRow}>
                  <span class={s.confirmText}>Delete this event?</span>
                  <span class={s.dialogActionsSpacer} />
                  <button
                    type="button"
                    class={s.cancelBtn}
                    disabled={saving()}
                    onClick={() => setConfirmDelete(false)}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    class={s.dangerBtnSolid}
                    disabled={saving()}
                    onClick={async () => {
                      await onDelete(editingId()!);
                      close();
                    }}
                  >
                    {saving() ? "…" : "Confirm"}
                  </button>
                </div>
              </Show>
            </form>
          </div>
        </div>
      </Portal>
    </Show>
  );
}
